import { all, takeLatest, put, call, select } from 'redux-saga/effects';

import { TUTOR } from '@dragonfruit/constants/userRoles';
import {
  ALERT_SUCCESS_THEME,
  ALERT_ERROR_THEME,
  ALERT_SYSTEMINFO_THEME,
} from '@dragonfruit/constants/alertBannerThemes';
import { FREE } from '@dragonfruit/constants/consumerTypes';

import { showAlertBanner } from '@dragonfruit/redux/alertBanner';
import { actions as errorActions } from '@dragonfruit/redux/error';
import * as actions from '@dragonfruit/redux/profile';

import { transformToOptionFormat } from '@dragonfruit/utils/tutorAuditFields';
import {
  apiFetchUserInfo,
  apiFetchStudentQuotas,
  apiFetchTutorStatisticalData,
  apiUpdateUserAvatar,
  apiPatchTutorProgressFinished,
  apiGetAccountStatus,
} from '@dragonfruit/services/apis';

export function* updateUserProfileInfo({
  data: {
    subscriptionQuota,
    withoutSubscriptionQuota: tokenQuota,
    tutorCategory,
    tutorProgressFinished,
    user: {
      firstName,
      lastName,
      snapaskUid,
      schoolName,
      major,
      college,
      verifiedSchool: schoolVerified,
      profilePicUrl: avatar,
      countryCode,
      unconfirmedEmail,
      unconfirmedPhone,
      phoneConfirmedAt,
      tutorIntroduction: tutorDescription,
      dob: birthday,
      referralCode,
      roleStatus,
      role: { name: role },
      region: { id: regionId, name: region },
      gender,
      phone,
      email,
      username,
      school,
      isProfileCompleted,
      profileCompletionMessage,
    } = {},
  } = {},
}) {
  const currentSchool = (school && school.name) || schoolName || '';
  const { district: schoolDistrict } = school || {};
  const schoolId = school && school.id;
  const userId = snapaskUid.toString();

  yield put(
    actions.fetchUserSuccess({
      userId,
      schoolId,
      school: currentSchool,
      schoolDistrict,
      major: transformToOptionFormat(major),
      college: transformToOptionFormat(college),
      role,
      firstName,
      lastName,
      gender,
      birthday,
      region,
      regionId,
      schoolVerified,
      phone,
      phoneConfirmedAt,
      countryCode,
      unconfirmedEmail,
      unconfirmedPhone,
      email,
      roleStatus,
      username,
      avatar,
      subscriptionQuota,
      tokenQuota,
      referralCode,
      tutorDescription,
      isProfileCompleted,
      profileCompletionMessage,
      tutorCategory,
      tutorProgressFinished,
    })
  );
}

export function* fetchUserInfo() {
  const userId = yield select(actions.getUserID);

  try {
    const response = yield call(apiFetchUserInfo, {
      userId,
    });

    yield call(updateUserProfileInfo, response);

    // student who not paid and not complete profile, need to show message
    const role = yield select(actions.getRole);

    if (role === TUTOR) {
      return;
    }

    const { consumerType } = yield call(apiFetchStudentQuotas, userId);
    const isPaidUser = consumerType !== FREE;

    yield put(actions.fetchQuotasSuccess(isPaidUser));

    if (isPaidUser) {
      return;
    }

    const isProfileCompleted = yield select(actions.getIsProfileCompleted);
    if (!isProfileCompleted) {
      yield put(
        showAlertBanner({
          message: 'account:account.requireFieldsForFreeQuota',
          theme: ALERT_SYSTEMINFO_THEME,
          keepAlive: true,
        })
      );
    }
  } catch (err) {
    yield put(actions.fetchUserError(err.message));
  }
}

export function* getTutorStatisticalData(params, request) {
  const userId = yield select(actions.getProfileInfo, 'userId');
  try {
    const response = yield call(
      apiFetchTutorStatisticalData,
      { userId },
      request
    );

    const {
      averageRate,
      answerCount,
      sinceDate,
      tutorBadges,
      curriculums,
    } = response.data;

    yield put(
      actions.getTutorStatisticalDataSuccess({
        averageRate: Math.round(averageRate * 100) / 100,
        answerCount,
        tutorBadges,
        curriculums,
        sinceDate,
      })
    );
  } catch (err) {
    yield put(actions.getTutorStatisticalDataError(err.message));
    yield put(errorActions.sendError(err));
  }
}

export function* updateAvatarFlow(action) {
  const userId = yield select(actions.getProfileInfo, 'userId');
  const { newAvatar } = action.payload;
  try {
    const data = new FormData();
    data.append('new_profile_pic', newAvatar);

    const response = yield call(apiUpdateUserAvatar, {
      userId,
      data,
    });

    const { user: { profilePicUrl: avatar } = {} } = response.data;

    yield put(actions.updateAvatarSuccess(avatar));
    yield put(
      showAlertBanner({
        message: 'account.updateAttributesSuccess',
        theme: ALERT_SUCCESS_THEME,
      })
    );
  } catch (err) {
    yield put(
      showAlertBanner({
        message: err.message,
        theme: ALERT_ERROR_THEME,
      })
    );
    yield put(actions.updateAvatarError(err.message));
  }
}

export function* getAccountStatusFlow() {
  const userId = yield select(actions.getUserID);

  try {
    yield call(apiGetAccountStatus, userId);
    yield put(actions.fetchAccountStatusSuccess());
  } catch (error) {
    yield put(actions.fetchAccountStatusError(error.message));
  }
}

export function* afterUpdateUserProfile(
  prevIsProfileCompleted,
  message,
  prevProfileCompletionMessage
) {
  const isProfileCompleted = yield select(actions.getIsProfileCompleted);
  const extraQuota = yield select(actions.getExtraQuotaByCompletingProfile);

  const showCompletedProfile =
    prevProfileCompletionMessage &&
    extraQuota > 0 &&
    isProfileCompleted &&
    !prevIsProfileCompleted;

  if (showCompletedProfile) {
    yield put(actions.showUserCompletedProfileDialog());

    return;
  }

  yield put(
    showAlertBanner({
      message,
      theme: ALERT_SUCCESS_THEME,
    })
  );
}

export function* setTutorProgressFinishedFlow() {
  try {
    yield call(apiPatchTutorProgressFinished);
  } catch (err) {}
}

export default function*() {
  yield all([
    takeLatest(actions.FETCH_USER, fetchUserInfo),
    takeLatest(actions.GET_TUTOR_STATISTICAL_DATA, getTutorStatisticalData),
    takeLatest(actions.UPDATE_AVATAR, updateAvatarFlow),
    takeLatest(actions.GET_ACCOUNT_STATUS, getAccountStatusFlow),
    takeLatest(
      actions.SET_TUTOR_PROGRESS_FINISHED,
      setTutorProgressFinishedFlow
    ),
  ]);
}



// WEBPACK FOOTER //
// ./app/sagas/profile.js